import {
  Component, OnInit, OnDestroy,
  ChangeDetectionStrategy, signal, computed,
  ViewChild, ElementRef, ChangeDetectorRef, inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as echarts from 'echarts';
import { ApiService } from '../../core/services/api.service';
import { ThemeService } from '../../core/services/theme.service';

const DAY_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('barChart',     { static: false }) barChartRef!:     ElementRef;
  @ViewChild('lineChart',    { static: false }) lineChartRef!:    ElementRef;
  @ViewChild('monthlyChart', { static: false }) monthlyChartRef!: ElementRef;
  @ViewChild('heatmapChart', { static: false }) heatmapChartRef!: ElementRef;
  @ViewChild('sourceChart',  { static: false }) sourceChartRef!:  ElementRef;
  @ViewChild('peakChart',    { static: false }) peakChartRef!:    ElementRef;

  dashboard      = signal<any>(null);
  company        = signal<any>(null);
  branch         = signal<any>(null);
  sources        = signal<any[]>([]);
  selectedSource = signal<any>(null);   // null = Universal
  loading        = signal(true);
  error          = signal('');
  companyId      = '';
  branchId       = '';

  readonly todayISO: string = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm   = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd   = d.getDate().toString().padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  })();

  selectedDate = signal<string>(this.todayISO);

  private barInstance:     echarts.ECharts | null = null;
  private lineInstance:    echarts.ECharts | null = null;
  private monthlyInstance: echarts.ECharts | null = null;
  private heatmapInstance: echarts.ECharts | null = null;
  private sourceInstance:  echarts.ECharts | null = null;
  private peakInstance:    echarts.ECharts | null = null;
  private chartsData: any  = null;
  private themeObserver: MutationObserver | null = null;

  private themeService = inject(ThemeService);
  private cdr          = inject(ChangeDetectorRef);
  private api          = inject(ApiService);
  private router       = inject(Router);
  private route        = inject(ActivatedRoute);

  // ── Computed ──────────────────────────────────────────────────────────────

  avgDurationFormatted = computed(() => {
    const d = this.dashboard();
    if (!d || !d.averageDurationSeconds) return '—';
    const secs = Math.round(d.averageDurationSeconds);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const rem  = secs % 60;
    return rem > 0 ? `${mins}m ${rem}s` : `${mins}m`;
  });

  peakHour = computed(() => {
    const hourly: any[] = this.dashboard()?.hourly ?? [];
    if (!hourly.length) return '—';
    const peak = hourly.reduce((best, cur) => cur.count > best.count ? cur : best);
    if (peak.count === 0) return '—';
    const h  = peak.hour.toString().padStart(2, '0');
    const h1 = (peak.hour + 1).toString().padStart(2, '0');
    return `${h}:00 – ${h1}:00`;
  });

  peakHourCount = computed(() => {
    const hourly: any[] = this.dashboard()?.hourly ?? [];
    return hourly.length ? Math.max(...hourly.map((h: any) => h.count)) : 0;
  });

  isUniversal = computed(() => this.selectedSource() === null);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit() {
    this.companyId = this.route.snapshot.paramMap.get('companyId') || '';
    this.branchId  = this.route.snapshot.paramMap.get('branchId')  || '';

    this.api.getCompany(this.companyId).subscribe({
      next: c => { this.company.set(c); this.cdr.markForCheck(); },
      error: () => {}
    });
    this.api.getBranch(this.companyId, this.branchId).subscribe({
      next: b => { this.branch.set(b); this.cdr.markForCheck(); },
      error: () => {}
    });
    this.api.getSources(this.companyId, this.branchId).subscribe({
      next: s => { this.sources.set(s); this.cdr.markForCheck(); },
      error: () => {}
    });

    this.loadDashboard();

    this.themeObserver = new MutationObserver(() => {
      if (this.chartsData) requestAnimationFrame(() => this.disposeAndReinit());
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  }

  ngOnDestroy() {
    this.themeObserver?.disconnect();
    this.disposeAll();
  }

  // ── Source selector ───────────────────────────────────────────────────────

  selectSource(source: any | null) {
    this.selectedSource.set(source);
    this.loading.set(true);
    this.dashboard.set(null);
    this.disposeAll();
    this.cdr.markForCheck();
    this.loadDashboard();
  }

  onDateChange(value: string) {
    this.selectedDate.set(value);
    this.loading.set(true);
    this.dashboard.set(null);
    this.disposeAll();
    this.cdr.markForCheck();
    this.loadDashboard();
  }

  private loadDashboard() {
    const sid  = this.selectedSource()?.idSource?.toString();
    const date = this.selectedDate();
    this.api.getDashboard(this.companyId, this.branchId, sid, date).subscribe({
      next: (data) => {
        this.chartsData = data;
        this.dashboard.set(data);
        this.loading.set(false);
        this.cdr.markForCheck();
        setTimeout(() => this.initAllCharts(data), 0);
      },
      error: () => {
        this.error.set('Error al cargar el dashboard');
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  // ── Chart helpers ─────────────────────────────────────────────────────────

  private palette() {
    const s = getComputedStyle(document.documentElement);
    const g = (v: string) => s.getPropertyValue(v).trim();
    return {
      border:        g('--border'),
      label:         g('--text-secondary'),
      tooltipBg:     g('--bg-surface'),
      tooltipText:   g('--text-primary'),
      tooltipBorder: g('--border'),
    };
  }

  private disposeAll() {
    this.barInstance?.dispose();
    this.lineInstance?.dispose();
    this.monthlyInstance?.dispose();
    this.heatmapInstance?.dispose();
    this.sourceInstance?.dispose();
    this.peakInstance?.dispose();
    this.barInstance = this.lineInstance = this.monthlyInstance =
      this.heatmapInstance = this.sourceInstance = this.peakInstance = null;
  }

  private disposeAndReinit() {
    this.disposeAll();
    if (this.chartsData) this.initAllCharts(this.chartsData);
  }

  private initAllCharts(data: any) {
    this.initBarChart(data);
    this.initLineChart(data);
    this.initMonthlyChart(data);
    this.initHeatmapChart(data);
    this.initSourceChart(data);
    this.initPeakHourChart(data);
  }

  // ── Bar chart: personas por hora hoy ─────────────────────────────────────

  private initBarChart(data: any) {
    if (!this.barChartRef?.nativeElement) return;
    const p = this.palette();
    this.barInstance = echarts.init(this.barChartRef.nativeElement, null, { renderer: 'canvas' });

    const hours  = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
    const counts = Array(24).fill(0);
    (data.hourly || []).forEach((h: any) => { counts[h.hour] = h.count; });

    this.barInstance.setOption({
      backgroundColor: 'transparent',
      grid: { left: '3%', right: '3%', bottom: '3%', top: '8%', containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: p.tooltipBg, borderColor: p.tooltipBorder,
        textStyle: { color: p.tooltipText, fontSize: 12 },
        formatter: (params: any) => `${params[0].axisValue}<br/><b>${params[0].value}</b> personas`
      },
      xAxis: {
        type: 'category', data: hours,
        axisLine: { lineStyle: { color: p.border } },
        axisLabel: { color: p.label, fontSize: 11, formatter: (v: string) => v.split(':')[0] }
      },
      yAxis: {
        type: 'value', minInterval: 1, axisLine: { show: false },
        splitLine: { lineStyle: { color: p.border, opacity: 0.6 } },
        axisLabel: { color: p.label, fontSize: 11 }
      },
      series: [{
        type: 'bar', data: counts,
        itemStyle: { color: '#3B82F6', borderRadius: [4, 4, 0, 0] },
        emphasis: { itemStyle: { color: '#F59E0B' } }
      }]
    });
  }

  // ── Line chart: personas por día esta semana ──────────────────────────────

  private initLineChart(data: any) {
    const weekly = data.weekly || [];
    if (!weekly.length || !this.lineChartRef?.nativeElement) return;
    const p = this.palette();
    this.lineInstance = echarts.init(this.lineChartRef.nativeElement, null, { renderer: 'canvas' });

    this.lineInstance.setOption({
      backgroundColor: 'transparent',
      grid: { left: '3%', right: '3%', bottom: '3%', top: '8%', containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: p.tooltipBg, borderColor: p.tooltipBorder,
        textStyle: { color: p.tooltipText, fontSize: 12 },
        formatter: (params: any) => `${params[0].axisValue}<br/><b>${params[0].value}</b> personas`
      },
      xAxis: {
        type: 'category', data: weekly.map((w: any) => w.dayName),
        axisLine: { lineStyle: { color: p.border } },
        axisLabel: { color: p.label, fontSize: 12 }
      },
      yAxis: {
        type: 'value', minInterval: 1, axisLine: { show: false },
        splitLine: { lineStyle: { color: p.border, opacity: 0.6 } },
        axisLabel: { color: p.label, fontSize: 11 }
      },
      series: [{
        type: 'line', data: weekly.map((w: any) => w.count), smooth: true,
        lineStyle: { color: '#10B981', width: 2 },
        itemStyle: { color: '#10B981' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16,185,129,0.25)' },
              { offset: 1, color: 'rgba(16,185,129,0)' }
            ]
          }
        },
        symbol: 'circle', symbolSize: 8
      }]
    });
  }

  // ── Line chart: trend mensual (últimos 30 días) ───────────────────────────

  private initMonthlyChart(data: any) {
    const monthly = data.monthly || [];
    if (!monthly.length || !this.monthlyChartRef?.nativeElement) return;
    const p = this.palette();
    this.monthlyInstance = echarts.init(this.monthlyChartRef.nativeElement, null, { renderer: 'canvas' });

    const labels = monthly.map((m: any) => {
      const [, mm, dd] = m.date.split('-');
      return `${dd}/${mm}`;
    });

    this.monthlyInstance.setOption({
      backgroundColor: 'transparent',
      grid: { left: '3%', right: '3%', bottom: '3%', top: '8%', containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: p.tooltipBg, borderColor: p.tooltipBorder,
        textStyle: { color: p.tooltipText, fontSize: 12 },
        formatter: (params: any) => `${params[0].axisValue}<br/><b>${params[0].value}</b> personas`
      },
      xAxis: {
        type: 'category', data: labels,
        axisLine: { lineStyle: { color: p.border } },
        axisLabel: { color: p.label, fontSize: 10, interval: 4, rotate: 30 }
      },
      yAxis: {
        type: 'value', minInterval: 1, axisLine: { show: false },
        splitLine: { lineStyle: { color: p.border, opacity: 0.6 } },
        axisLabel: { color: p.label, fontSize: 11 }
      },
      series: [{
        type: 'line', data: monthly.map((m: any) => m.count), smooth: true,
        lineStyle: { color: '#8B5CF6', width: 2 },
        itemStyle: { color: '#8B5CF6' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(139,92,246,0.25)' },
              { offset: 1, color: 'rgba(139,92,246,0)' }
            ]
          }
        },
        symbol: 'none'
      }]
    });
  }

  // ── Heatmap: por hora × día de semana ─────────────────────────────────────

  private initHeatmapChart(data: any) {
    if (!this.heatmapChartRef?.nativeElement) return;
    const p = this.palette();

    const dateStr = this.selectedDate();
    const selectedDateObj = new Date(dateStr + 'T00:00:00');
    const jsDay = selectedDateObj.getDay(); // 0=Dom, 1=Lun, ..., 6=Sáb
    // Convierte JS day (0=Dom) a nuestro índice (0=Lun...6=Dom)
    const selectedDayOfWeek = jsDay === 0 ? 6 : jsDay - 1;

    const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const getDateForDay = (dayOfWeek: number): string => {
      // Ocurrencia más reciente de ese dayOfWeek <= selectedDate
      const diff = (selectedDayOfWeek - dayOfWeek + 7) % 7;
      const d = new Date(selectedDateObj);
      d.setDate(d.getDate() - diff);
      const dd  = d.getDate().toString().padStart(2, '0');
      const mmm = MONTH_SHORT[d.getMonth()];
      return `${dd}/${mmm}`;
    };

    this.heatmapInstance = echarts.init(this.heatmapChartRef.nativeElement, null, { renderer: 'canvas' });

    const heatData = (data.heatmap || []).map((h: any) => [h.hour, h.dayOfWeek, h.count]);
    const maxVal   = Math.max(...heatData.map((d: any) => d[2]), 1);

    this.heatmapInstance.setOption({
      backgroundColor: 'transparent',
      grid: { left: '60px', right: '80px', bottom: '30px', top: '10px' },
      tooltip: {
        formatter: (params: any) => {
          const [hour, day, count] = params.data;
          const dateLabel = getDateForDay(day);
          return `${DAY_SHORT[day]} ${dateLabel} ${hour.toString().padStart(2, '0')}:00<br/><b>${count}</b> personas`;
        },
        backgroundColor: p.tooltipBg, borderColor: p.tooltipBorder,
        textStyle: { color: p.tooltipText, fontSize: 12 }
      },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')),
        axisLine: { lineStyle: { color: p.border } },
        axisLabel: { color: p.label, fontSize: 10 },
        splitArea: { show: true, areaStyle: { color: ['transparent'] } }
      },
      yAxis: {
        type: 'category', data: DAY_SHORT,
        axisLine: { lineStyle: { color: p.border } },
        axisLabel: { color: p.label, fontSize: 11 },
        splitArea: { show: true, areaStyle: { color: ['transparent'] } }
      },
      visualMap: {
        min: 0, max: maxVal, calculable: false,
        orient: 'vertical', right: '0', top: 'center',
        inRange: { color: ['transparent', '#3B82F6'] },
        textStyle: { color: p.label, fontSize: 10 },
        itemHeight: 100, itemWidth: 14
      },
      series: [{
        type: 'heatmap', data: heatData,
        itemStyle: { borderRadius: 2 },
        emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(59,130,246,0.4)' } }
      }]
    });
  }

  // ── Bar chart horizontal: personas por cámara hoy ─────────────────────────

  private initSourceChart(data: any) {
    const sources = data.bySource || [];
    if (!sources.length || !this.sourceChartRef?.nativeElement) return;
    const p = this.palette();
    this.sourceInstance = echarts.init(this.sourceChartRef.nativeElement, null, { renderer: 'canvas' });

    this.sourceInstance.setOption({
      backgroundColor: 'transparent',
      grid: { left: '3%', right: '10%', bottom: '3%', top: '3%', containLabel: true },
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'none' },
        backgroundColor: p.tooltipBg, borderColor: p.tooltipBorder,
        textStyle: { color: p.tooltipText, fontSize: 12 },
        formatter: (params: any) => `${params[0].name}<br/><b>${params[0].value}</b> personas`
      },
      xAxis: {
        type: 'value', minInterval: 1, axisLine: { show: false },
        splitLine: { lineStyle: { color: p.border, opacity: 0.6 } },
        axisLabel: { color: p.label, fontSize: 11 }
      },
      yAxis: {
        type: 'category', data: sources.map((s: any) => s.sourceName),
        axisLine: { lineStyle: { color: p.border } },
        axisLabel: { color: p.label, fontSize: 12 }
      },
      series: [{
        type: 'bar', data: sources.map((s: any) => s.count),
        itemStyle: { color: '#F59E0B', borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: p.label, fontSize: 11 }
      }]
    });
  }

  // ── Bar chart: hora pico por día de la semana ─────────────────────────────

  private computePeakHours(heatmap: any[]) {
    return Array.from({ length: 7 }, (_, day) => {
      const dayEntries = heatmap.filter((h: any) => h.dayOfWeek === day);
      if (!dayEntries.length) return { day, hour: -1, count: 0 };
      const peak = dayEntries.reduce((best: any, cur: any) => (cur.count > best.count ? cur : best));
      return { day, hour: peak.hour, count: peak.count };
    });
  }

  private initPeakHourChart(data: any) {
    if (!this.peakChartRef?.nativeElement) return;
    const p = this.palette();
    this.peakInstance = echarts.init(this.peakChartRef.nativeElement, null, { renderer: 'canvas' });

    // Filtrar días futuros de la semana: solo mostrar hasta el día actual (0=Lun, ..., 6=Dom)
    const dateStr = this.selectedDate();
    const selectedDateObj = new Date(dateStr + 'T00:00:00');
    const jsDay = selectedDateObj.getDay(); // 0=Dom, 1=Lun, ..., 6=Sáb
    const maxWeekday = jsDay === 0 ? 6 : jsDay - 1; // convierte a 0=Lun, ..., 6=Dom

    const allPeaks = this.computePeakHours(data.heatmap || []);

    // Override: para el día de la fecha seleccionada, usar hourly real en lugar del heatmap histórico
    const hourly: any[] = data.hourly || [];
    if (hourly.length) {
      const todayPeak = hourly.reduce((best: any, cur: any) =>
        cur.count > best.count ? cur : best
      );
      if (todayPeak.count > 0) {
        const entry = allPeaks.find((pk: any) => pk.day === maxWeekday);
        if (entry) {
          entry.hour  = todayPeak.hour;
          entry.count = todayPeak.count;
        }
      }
    }

    const peaks = allPeaks.filter((pk) => pk.day <= maxWeekday);

    const counts     = peaks.map((pk) => pk.count);
    const hourLabels = peaks.map((pk) =>
      pk.hour === -1 ? '—' : `${pk.hour.toString().padStart(2, '0')}:00`
    );

    this.peakInstance.setOption({
      backgroundColor: 'transparent',
      grid: { left: '3%', right: '3%', bottom: '3%', top: '18%', containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: p.tooltipBg, borderColor: p.tooltipBorder,
        textStyle: { color: p.tooltipText, fontSize: 12 },
        formatter: (params: any) => {
          const idx = params[0].dataIndex;
          return `${DAY_SHORT[peaks[idx].day]}<br/>Hora pico: <b>${hourLabels[idx]}</b><br/><b>${params[0].value}</b> personas`;
        }
      },
      xAxis: {
        type: 'category', data: peaks.map((pk) => DAY_SHORT[pk.day]),
        axisLine: { lineStyle: { color: p.border } },
        axisLabel: { color: p.label, fontSize: 12 }
      },
      yAxis: {
        type: 'value', minInterval: 1, axisLine: { show: false },
        splitLine: { lineStyle: { color: p.border, opacity: 0.6 } },
        axisLabel: { color: p.label, fontSize: 11 }
      },
      series: [{
        type: 'bar', data: counts,
        itemStyle: { color: '#6366F1', borderRadius: [4, 4, 0, 0] },
        emphasis: { itemStyle: { color: '#4338CA' } },
        label: {
          show: true, position: 'top', color: p.label, fontSize: 11,
          formatter: (params: any) => hourLabels[params.dataIndex]
        }
      }]
    });
  }

  goToCompanies() { this.router.navigate(['/companies']); }
  goToBranches()  { this.router.navigate(['/companies', this.companyId, 'branches']); }
}
